import { useCallback } from 'react';
import { useMixpanel, useStrategiesContext } from '../../contexts';
import {
  ExecutionReport,
  isCareExecutionReport,
  type CareExecutionReport,
  type CustomerExecutionReport,
} from '../../types';
import { ParametersTable } from '../ParametersTable';
import { Side } from '../Side';
import { SmartSummary, type SmartSummaryProps } from '../SmartSummary';
import { Table, Tbody, Td, Th, Tr } from '../Table';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import { Header, Wrapper } from './styles';

import { defineMessages } from 'react-intl';
import { MixpanelEvent } from '../../tokens/mixpanel';
import { isEntityRFQ } from '../../utils/order';
import { Flex } from '../Core';
import { FormattedMessage } from '../Intl';
import { ExecutionReportDifference } from './ExecutionReportDifference';
import { useDifferenceBetweenExecutionReports } from './ExecutionReportDifference/utils';

const messages = defineMessages({
  allowedSlippage: {
    defaultMessage: 'Allowed slippage',
    id: 'ExecutionReportSummary.allowedSlippage',
  },
  diff: {
    defaultMessage: 'Diff',
    id: 'ExecutionReportSummary.diff',
  },
  none: {
    defaultMessage: 'None',
    id: 'ExecutionReportSummary.none',
  },
  params: {
    defaultMessage: 'Params',
    id: 'ExecutionReportSummary.params',
  },
  memberCareOrderIds: {
    defaultMessage: 'Member Care Order IDs',
    id: 'ExecutionReportSummary.memberCareOrderIds',
  },
  rfq: {
    defaultMessage: 'RFQ',
    id: 'ExecutionReportSummary.rfq',
  },
});

export function ExecutionReportSummary({
  executionReport,
  prevExecutionReport,
  showDiff = false,
  ...props
}: {
  executionReport: ExecutionReport | CustomerExecutionReport | CareExecutionReport;
  prevExecutionReport?: ExecutionReport | CustomerExecutionReport | CareExecutionReport;
  showDiff?: boolean;
} & Omit<SmartSummaryProps, 'entity' | 'type'>) {
  const { strategiesByName } = useStrategiesContext();
  const strategy = strategiesByName.get((executionReport as ExecutionReport)?.Strategy || '');
  const isRFQ = isEntityRFQ(executionReport as ExecutionReport);
  const isCare = isCareExecutionReport(executionReport);

  const isReportsComparable =
    executionReport instanceof ExecutionReport && prevExecutionReport instanceof ExecutionReport;

  const differences = useDifferenceBetweenExecutionReports(
    isReportsComparable ? executionReport : undefined,
    isReportsComparable ? prevExecutionReport : undefined
  );

  const mixpanel = useMixpanel();
  const handleOnViewDiff = useCallback(() => {
    mixpanel.track(MixpanelEvent.ViewTimelineDiff);
  }, [mixpanel]);

  return (
    <Wrapper>
      <Header>
        <div style={{ display: 'inline-flex', gap: '5px' }}>
          <Side side={executionReport.Side}>{executionReport.Side}</Side>
          {executionReport.Symbol}
          <Text color="colorTextMuted" style={{ textTransform: 'uppercase' }}>
            {isRFQ ? <FormattedMessage {...messages.rfq} /> : strategy?.DisplayName}
          </Text>
        </div>
        <Flex flexDirection="row" gap="spacing8">
          {!!differences?.length && showDiff && (
            <Tooltip
              tooltip={<ExecutionReportDifference differences={differences} />}
              onOpen={handleOnViewDiff}
              placement="bottom"
              showUnderline={true}
              usePortal
            >
              <Text size="fontSizeSmall" color="colorTextSubtle">
                <FormattedMessage {...messages.diff} />
              </Text>
            </Tooltip>
          )}
          <Tooltip
            showUnderline={true}
            tooltip={
              isRFQ ? (
                <Table>
                  <Tbody>
                    <Tr>
                      <Th>
                        <FormattedMessage {...messages.allowedSlippage} />
                      </Th>
                      <Td>
                        {(executionReport as ExecutionReport).AllowedSlippage ?? (
                          <Text color="colorTextMuted">
                            <FormattedMessage {...messages.none} />
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              ) : isCare ? (
                <CareOrderParametersList executionReport={executionReport} />
              ) : (
                <ParametersTable order={executionReport} />
              )
            }
            placement="bottom"
          >
            <Text color="colorTextSubtle" size="fontSizeSmall">
              <FormattedMessage {...messages.params} />
            </Text>
          </Tooltip>
        </Flex>
      </Header>
      <SmartSummary entity={executionReport} type="order" showSide={false} showLastMarket={true} {...props} />
    </Wrapper>
  );
}

function CareOrderParametersList({ executionReport }: { executionReport: CareExecutionReport }) {
  const memberCareOrders = executionReport.MemberCareOrders ?? [];

  if (memberCareOrders.length === 0) {
    return null;
  }

  return (
    <Flex flexDirection="column" gap="spacing4">
      <Text color="colorTextSubtle" size="fontSizeSmall">
        <FormattedMessage {...messages.memberCareOrderIds} />
      </Text>
      {memberCareOrders.map(memberCareOrder => (
        <Text key={memberCareOrder.OrderID} color="colorTextImportant" size="fontSizeSmall">
          {memberCareOrder.OrderID}
        </Text>
      ))}
    </Flex>
  );
}
